import React, { Component, FormEvent, ReactNode } from "react";
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Alert,
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledAccordion,
} from "reactstrap";
import service, {
  TurmaImportarItem,
  TurmaImportarResponse,
  TurmaResponse,
} from "../../api/turmas";
import { errorToString } from "../../helpers/funcoes";
import { Titulo } from "../Titulo";
import "./TurmasPage.css";

interface TurmasPageState {
  loading: boolean;
  uploading: boolean;
  modalVisible: boolean;
  turmas: TurmaResponse[];
  deltas: TurmaImportarGroup[];
  error: string | null;
  selectedFile?: File;
}

interface TurmaImportarGroup {
  toAdd?: TurmaImportarItem;
  toDelete?: TurmaImportarItem;
}

export class TurmasPage extends Component<
  Record<string, unknown>,
  TurmasPageState
> {
  constructor(props: Record<string, unknown>) {
    super(props);
    this.state = {
      loading: false,
      uploading: false,
      modalVisible: false,
      turmas: [],
      deltas: [],
      error: null,
    };
  }

  componentDidMount(): void {
    this.loadAlunos();
  }

  onFileChanged(fileList: FileList | null) {
    this.setState({
      selectedFile: fileList && fileList.length > 0 ? fileList[0] : undefined,
    });
  }

  onToggleModal() {
    this.setState({
      modalVisible: !this.state.modalVisible,
    });
  }

  loadAlunos() {
    this.setState({
      loading: true,
      error: null,
    });
    service
      .getAll()
      .then((res) => {
        this.setState({
          loading: false,
          turmas: res,
        });
      })
      .catch((err) => {
        this.setState({
          loading: true,
          error: errorToString(err),
        });
      });
  }

  uploadAlunos(e: FormEvent) {
    e.preventDefault();
    const selectedFile = this.state.selectedFile;
    if (selectedFile) {
      this.setState({
        uploading: true,
        modalVisible: true,
        error: null,
      });
      service
        .importarPrevia(selectedFile)
        .then((res) => {
          if (!this.state.modalVisible) return;
          if (!res.toAdd.length && !res.toDelete.length) {
            throw new Error("Nenhuma alteração encontrada.");
          }
          const table: Record<string, TurmaImportarGroup> = {};
          for (const i of res.toAdd) {
            table[i.matricula] ??= {};
            table[i.matricula].toAdd = i;
          }
          for (const i of res.toDelete) {
            table[i.matricula] ??= {};
            table[i.matricula].toDelete = i;
          }
          const deltas = Object.values(table);
          deltas.sort((a, b) => {
            if (a.toAdd) {
              return b.toAdd ? a.toAdd.nome.localeCompare(b.toAdd.nome) : -1;
            }
            if (a.toDelete) {
              return b.toDelete
                ? a.toDelete.nome.localeCompare(b.toDelete.nome)
                : -1;
            }
            return b.toAdd || b.toDelete ? 1 : 0;
          });
          this.setState({
            uploading: false,
            deltas,
          });
        })
        .catch((err) => {
          this.setState({
            uploading: false,
            modalVisible: false,
            error: errorToString(err),
          });
        });
    }
  }

  submitAlunos() {
    const deltas = this.state.deltas;
    if (!deltas.length) return;
    this.setState({
      uploading: true,
    });
    const payload: TurmaImportarResponse = {
      toAdd: [],
      toDelete: [],
    };
    for (const i of deltas) {
      if (i.toAdd) payload.toAdd.push(i.toAdd);
      if (i.toDelete) payload.toDelete.push(i.toDelete);
    }
    service
      .importarFinal(payload)
      .then(() => {
        this.setState({
          uploading: false,
          modalVisible: false,
          deltas: [],
        });
        this.loadAlunos();
      })
      .catch((err) => {
        this.setState({
          uploading: false,
          modalVisible: false,
          error: errorToString(err),
        });
      });
  }

  render(): ReactNode {
    return (
      <div>
        <Titulo text="Turmas" />
        {this.state.error && (
          <Alert color="danger" data-cy="alert">
            {this.state.error}
          </Alert>
        )}
        <h3>Atualizar lista</h3>
        {this._buildModal()}
        <form onSubmit={(e) => this.uploadAlunos(e)}>
          <ul>
            <li>Arquivo do Excel (*.xlsx)</li>
            <li>Quatro colunas: Matrícula, Nome, Turma, Ano</li>
          </ul>
          <Row>
            <Col xs={9} sm={4}>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) => this.onFileChanged(e.target.files)}
                required
              />
            </Col>
            <Col>
              <Button
                color={this.state.selectedFile ? "primary" : "secondary"}
                type="submit"
              >
                Enviar
              </Button>
            </Col>
          </Row>
        </form>
        <h3 className="mt-4">Lista de alunos</h3>
        {this.state.loading ? (
          <Spinner />
        ) : (
          <>
            {!this.state.turmas.length && <span>Nenhum aluno encontrado.</span>}
            <UncontrolledAccordion open={[]}>
              {this.state.turmas.map((t) => (
                <AccordionItem key={t.id}>
                  <AccordionHeader targetId={t.id}>{t.nome}</AccordionHeader>
                  <AccordionBody accordionId={t.id}>
                    <ol>
                      {t.alunos?.map((a) => (
                        <li key={a.id}>{a.nome}</li>
                      ))}
                    </ol>
                  </AccordionBody>
                </AccordionItem>
              ))}
            </UncontrolledAccordion>
          </>
        )}
      </div>
    );
  }

  _buildModal() {
    function deltaCell(toAdd?: string, toDelete?: string) {
      if (toAdd && toDelete) {
        if (toAdd == toDelete) {
          return <span>{toAdd}</span>;
        }
        return (
          <>
            <span className="deltaToAdd">{toAdd}</span>
            <br />
            <span className="deltaToDelete">{toDelete}</span>
          </>
        );
      }
      if (toAdd) {
        return <span className="deltaToAdd">{toAdd}</span>;
      }
      if (toDelete) {
        return <span className="deltaToDelete">{toDelete}</span>;
      }
      return <></>;
    }

    return (
      <Modal
        isOpen={this.state.modalVisible}
        toggle={() => this.onToggleModal()}
        centered
      >
        <ModalHeader toggle={() => this.onToggleModal()}>
          Importar Alunos
        </ModalHeader>
        <ModalBody>
          {this.state.uploading ? (
            <div className="text-center">
              <Spinner />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Matrícula</th>
                  <th>Aluno</th>
                  <th>Turma</th>
                  <th>Ano</th>
                </tr>
              </thead>
              <tbody>
                {this.state.deltas.map((i, idx) => (
                  <tr key={idx}>
                    <td>
                      {deltaCell(i.toAdd?.matricula, i.toDelete?.matricula)}
                    </td>
                    <td>{deltaCell(i.toAdd?.nome, i.toDelete?.nome)}</td>
                    <td>{deltaCell(i.toAdd?.turma, i.toDelete?.turma)}</td>
                    <td>
                      {deltaCell(
                        i.toAdd?.ano.toString(),
                        i.toDelete?.ano.toString()
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        {!this.state.uploading && (
          <ModalFooter>
            <Button
              type="button"
              color="primary"
              onClick={() => this.submitAlunos()}
            >
              Confirmar
            </Button>
          </ModalFooter>
        )}
      </Modal>
    );
  }
}
